import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Globe, Lock, SlidersHorizontal, Headphones, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IMG } from "@/lib/site";

const CATS = {
  calendars: { title: "Premium Calendars", items: [
    { name: "2025 Wall Calendar", img: IMG.store.wall, pos: "object-[center_30%]" },
    { name: "Desk Calendar", img: IMG.store.desk },
    { name: "Limited Edition Set", img: IMG.store.limited, contain: true },
  ] },
  posters: { title: "Posters", items: [
    { name: "A2 Editorial Poster", img: IMG.products[3] },
    { name: "Signed Print", img: IMG.products[0] },
    { name: "Collector Series", img: IMG.products[1] },
  ] },
  prints: { title: "Fine Art Prints", items: [
    { name: "Framed Gallery Print", img: IMG.products[4] },
    { name: "Canvas Print", img: IMG.products[5] },
    { name: "Metal Print", img: IMG.products[2] },
  ] },
  merch: { title: "Merchandise", items: [
    { name: "Branded Apparel", img: IMG.products[0] },
    { name: "Accessories", img: IMG.products[3] },
    { name: "Exclusive Drops", img: IMG.products[1] },
  ] },
};

const PERKS = [
  { icon: ShoppingBag, t: "Premium Quality", d: "Printed on high-quality materials." },
  { icon: Globe, t: "Worldwide Shipping", d: "Fast and reliable delivery." },
  { icon: Lock, t: "Secure Checkout", d: "Safe and encrypted transactions." },
  { icon: SlidersHorizontal, t: "Multiple Options", d: "Different sizes and product choices." },
  { icon: Headphones, t: "Support", d: "We're here to help every step of the way." },
];

export default function Store() {
  const [tab, setTab] = useState("calendars");
  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6" data-testid="store-hero">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="overline text-gold mb-4">Creator Store</p>
            <h1 className="font-display text-6xl md:text-8xl uppercase text-white leading-[0.9]">Premium<br />Merch & Prints</h1>
            <p className="mt-6 text-white/60 text-lg max-w-md">Support your favorite creators with high-quality products designed to celebrate their brand.</p>
            <Tabs value={tab} onValueChange={setTab} className="mt-8">
              <TabsList className="bg-transparent gap-2 p-0 flex-wrap h-auto">
                {Object.keys(CATS).map((k) => (
                  <TabsTrigger key={k} value={k} data-testid={`store-tab-${k}`} className="border border-white/15 data-[state=active]:bg-gold data-[state=active]:text-black uppercase tracking-wider text-xs px-5 py-2.5 rounded-none text-white/80">
                    {k}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </Reveal>
          <Reveal delay={0.15} className="relative overflow-hidden grain">
            <img src={IMG.store.hero} alt="" className="w-full h-[460px] object-cover object-[center_35%]" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/20" />
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16">
        <Tabs value={tab} onValueChange={setTab}>
          {Object.entries(CATS).map(([k, v]) => (
            <TabsContent key={k} value={k} className="mt-0">
              <h2 className="font-display text-3xl uppercase text-white mb-6">{v.title}</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {v.items.map((it, i) => (
                  <div key={i} className="group border border-white/10 overflow-hidden bg-[#0b0b0b]" data-testid={`product-${k}-${i}`}>
                    <div className="overflow-hidden h-64 bg-black">
                      <img src={it.img} alt={it.name} className={`h-full w-full ${it.contain ? "object-contain" : `object-cover ${it.pos || "object-top"}`} group-hover:scale-105 transition-transform duration-700`} />
                    </div>
                    <div className="p-5 flex items-center justify-between">
                      <span className="font-cond uppercase tracking-wide text-white/90">{it.name}</span>
                      <span className="text-gold text-sm">View</span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <section className="border-y border-white/10 bg-[#0a0a0a] py-16">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {PERKS.map((p, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <p.icon size={30} strokeWidth={1.2} className="text-gold mb-4" />
              <h3 className="font-cond uppercase tracking-wide text-white mb-1">{p.t}</h3>
              <p className="text-white/50 text-sm">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-display text-5xl md:text-6xl uppercase text-white leading-[0.9]">Turn Content<br /><span className="text-gold">Into Income</span></h2>
            <p className="mt-5 text-white/60 max-w-md">Offer exclusive merchandise to your fans and build an additional revenue stream.</p>
            <ul className="mt-6 space-y-3">
              {["Calendars", "Posters & Prints", "Exclusive Merchandise", "And More"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-white/85"><Check size={18} className="text-gold" /> {x}</li>
              ))}
            </ul>
            <Link to="/pricing" data-testid="store-start" className="mt-8 inline-flex bg-gold text-black px-8 py-4 overline hover:bg-[#F5D061] transition-colors duration-300">Start Selling Today</Link>
          </Reveal>
          <Reveal delay={0.12} className="overflow-hidden">
            <img src={IMG.store.income} alt="" className="w-full h-[440px] object-cover object-[center_30%]" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
